import { useCallback, useEffect, useMemo, useState } from "react";

import { STORAGE_KEYS } from "@utils/storageKeys";

type Theme = "light" | "dark";

const isTheme = (value: string | null): value is Theme => {
  return value === "light" || value === "dark";
};

const storedTheme = localStorage.getItem(STORAGE_KEYS.THEME) as Theme | null;

export const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(
    isTheme(storedTheme) ? storedTheme : "light",
  );

  const isDarkMode = theme === "dark";
  const isLightMode = theme === "light";

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const newTheme = prev === "dark" ? "light" : "dark";
      localStorage.setItem(STORAGE_KEYS.THEME, newTheme);
      return newTheme;
    });
  }, []);

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.setAttribute("data-theme", "light");
    }

    if (storedTheme !== theme) {
      localStorage.setItem(STORAGE_KEYS.THEME, theme);
    }
  }, [theme]);

  return useMemo(
    () => ({ theme, isDarkMode, isLightMode, toggleTheme, setTheme }),
    [theme, isDarkMode, isLightMode, toggleTheme],
  );
};
